import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
  useMemo
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  menus: any
  setMenus: Dispatch<SetStateAction<any | undefined>>
  permissions: any
  setPermissions: Dispatch<SetStateAction<any | undefined>>
  logout: () => void
  allowedUrls: string[]
}


const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  menus: undefined,
  setMenus: () => {},
  permissions: undefined,
  setPermissions: () => {},
  logout: () => {},
  allowedUrls: [],
}


const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const [menus, setMenus] = useState<any | undefined>()
  const [permissions, setPermissions] = useState<any | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
  setAuth(auth)
  const menu:any = currentUser?.menuTree
  const permission:any = currentUser?.permissions
    if (auth) {
      setMenus(menu)
      setPermissions(permission)
      localStorage.setItem('softphone-connect-on-start','true');
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }
  const logout = () => {
    setPermissions(undefined)
    setMenus(undefined)
    saveAuth(undefined)
    setCurrentUser(undefined)
  }
 const allowedUrls = useMemo(() => {
    return menus ? flattenMenuUrls(menus) : []
  }, [menus])
  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        menus,
        setMenus,
        permissions,
        setPermissions,
        logout,
        allowedUrls
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser, setMenus, setPermissions} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const {data} = await getUserByToken(apiToken)
          const menu:any = data?.menuTree
          const permission:any = data?.permissions
          if (data) {
            setMenus(menu)
            setPermissions(permission)
            setCurrentUser(data)
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.api_token) {
      requestUser(auth.api_token)
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}
export const flattenMenuUrls = (menus: any[]): string[] => {
  let urls: string[] = []

  menus.forEach((menu) => {
    if (menu.url && menu.url !== '-') {
      urls.push(menu.url)
    }

    if (menu.children?.length) {
      urls = urls.concat(flattenMenuUrls(menu.children))
    }
  })

  return urls
}
export {AuthProvider, AuthInit, useAuth}
